import { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { linkKeyboardProps } from "../utils/keyboardActivation";
import {
    getAllUnit,
    fetchAlertsForFleet,
    fetchEnergyUsage,
} from "../lib/api";
import { useFleetStatusCheck } from "../hooks/useFleetStatusCheck";
import useFleetStore from "../utils/store";
import gotoUnits from "../assets/images/gotoUnits.png";
import { useNavigate } from "react-router-dom";
import DonutChart from "../components/Charts/DonutChart";
import RuntimeBarChart from "../components/Charts/EnergyUsageHorizontalBar";
import DualAxisLineChart from "../components/Charts/EnergyVsTemp";
import Daysfilterdropdown from "../components/Common/Daysfilterdropdown";
import LoadingModal from "../components/Common/LoadingModal";
import SummaryCard from "../components/Common/SummaryCard";
import WeatherWidget from '../components/WeatherWidget';
import { useLocation } from 'react-router-dom';
import gotoUnitsInverse from "../assets/images/gotoUnitsInverse.png";
import Button from "../components/Common/Button";
import { ALERT_VIDEOS } from "../utils/constants";
import AlertIcon from "../components/Common/AlertIcon";
import ConnectionStatusIcon from "../components/Common/ConnectionStatusIcon";
import CurrentUserContext from "../contexts/CurrentUserContext";
import {
    convertKWhToPreferredUnit,
    formatEnergyValue,
    getEnergyUnitLabel,
} from "../utils/energyUnit";
import {
    emptyFilters,
    writeUnitListQuery,
    ZONE_UNIT_LIST_QUERY,
} from "../utils/unitListQuery";
import { unitListReturnState } from "../utils/unitListReturn";

const escapeCsvValue = (value) => {
    const stringValue = String(value ?? "");

    if (/[",\n]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
};

const Dashboard = () => {
    const selectedFleet = useFleetStore((state) => state.selectedFleet);
    const { currentUser } = useContext(CurrentUserContext);
    const energyUnit = currentUser?.energyUnit;
    const energyUnitLabel = getEnergyUnitLabel(energyUnit);
    const [totalUnitsCount, setTotalUnitsCount] = useState(0);
    const [connectedUnitsCount, setConnectedUnitsCount] = useState(0);
    const [disconnectedUnitsCount, setDisconnectedUnitsCount] = useState(0);
    const [unitsFetching, setUnitsFetching] = useState(false);
    const [alarmsdata, setAlarmsData] = useState([]);
    const [energyUsage, setEnergyUsage] = useState();
    const [selectedOption, setSelectedOption] = useState("Last 24 Hrs");
    const [selectedOptionMin, setSelectedOptionMin] = useState("Last 24 Hrs");
    const [loader, setLoader] = useState(false);

    const location = useLocation();

    const navigate = useNavigate();

    const getAlertVideoUrl = (alertType) => {
        const normalizedType = alertType?.toLowerCase().trim() || "";
        if (normalizedType === "air filter") {
            return ALERT_VIDEOS.AIR_FILTER_VIDEO_URL;
        }
        if (normalizedType.startsWith("condensate")) {
            return ALERT_VIDEOS.CONDENSATE_VIDEO_URL;
        }
        return null;
    };

    const loadInitialPageData = useCallback(async () => {
        setLoader(true)
        setUnitsFetching(true);
        try {
            const [countAllResponse, countConnectedResponse, countDisconnectedResponse] = await Promise.all([
              getAllUnit({ pageNumber: 1, pageSize: 1, unid: selectedFleet?.unid, }),
              getAllUnit({ pageNumber: 1, pageSize: 1, unid: selectedFleet?.unid, connected: "true" }),
              getAllUnit({ pageNumber: 1, pageSize: 1, unid: selectedFleet?.unid, connected: "false" }),
            ]);
            const countAllResponseData = countAllResponse?.data?.data;
            const countConnectedResponseData = countConnectedResponse?.data?.data;
            const countDisconnectedResponseData = countDisconnectedResponse?.data?.data;
            setTotalUnitsCount(countAllResponseData?.count || 0);
            setConnectedUnitsCount(countConnectedResponseData?.count || 0);
            setDisconnectedUnitsCount(countDisconnectedResponseData?.count || 0);
            setLoader(false)
        } catch (error) {
            console.error("Error fetching units:", error);
            setTotalUnitsCount(0);
            setConnectedUnitsCount(0);
            setDisconnectedUnitsCount(0);
            setLoader(false)
        } finally {
            setUnitsFetching(false);
            setLoader(false)
        }
    }, [selectedFleet?.unid]);

    const GetEnergyUsage = useCallback(async () => {
        try {
            const response = await fetchEnergyUsage({
                unid: selectedFleet?.unid,
            });
            setEnergyUsage(response?.data);
        } catch (error) {
            console.error("Error fetching energy usage:", error);
        } finally {
            setUnitsFetching(false);
        }
    }, [selectedFleet?.unid]);

    useEffect(() => {
        const fleetAlerts = async () => {
            const payload = { pageNumber: 1, pageSize: 100, unid: selectedFleet.unid, status: 'active' }
            const response = await fetchAlertsForFleet(payload)
            setAlarmsData(response?.data?.alerts || [])
            setLoader(false)
        }
        if (selectedFleet?.unid) {
            fleetAlerts();
        }

    }, [selectedFleet])

    useEffect(() => {
        if (selectedFleet?.unid) {
            loadInitialPageData();
            GetEnergyUsage();
        }
    }, [selectedFleet, loadInitialPageData, GetEnergyUsage]);

    const handleNavigateToUnits = () => {
        navigate("/manageUnits");
    };

    const handleDonutSliceClick = (entry) => {
        const search = writeUnitListQuery("", ZONE_UNIT_LIST_QUERY, {
            filters: { ...emptyFilters(ZONE_UNIT_LIST_QUERY.filterSections), connected: entry?.connected ? 'true' : 'false' },
        });
        navigate(`/units${search}`);
    };

    const handleNavigateToAlarms = () => {
        navigate("/alerts");
    };

    const handleNavigateToUnit = (serialNumber) => {
        if (!serialNumber) return;
        navigate(`/units/${serialNumber}`, { state: unitListReturnState("dashboard") });
    };

    const ConnectedChartData = useMemo(() => ([
        { name: 'Connected Units', value: connectedUnitsCount, color: '#00C49F', connected: true },
        { name: 'Disconnected Units', value: disconnectedUnitsCount, color: '#FF6E76', connected: false },
    ]), [connectedUnitsCount, disconnectedUnitsCount]);

    const timeZone = selectedFleet?.timeZone;

    const processChartData = useCallback((data = []) =>
        data.map((item) => {
            const dateObj = new Date(item.timestamp);

            const month = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
            const day = String(dateObj.getUTCDate()).padStart(2, "0");

            let hours = dateObj.getUTCHours();
            const ampm = hours >= 12 ? "PM" : "AM";
            hours = hours % 12 || 12;

            const celsius = item.outdoorTempC;
            const fahrenheit = (celsius * 9) / 5 + 32;

            const roundedTemperature = fahrenheit >= 0
                ? Math.ceil(fahrenheit)
                : Math.floor(fahrenheit);

            return {
                dateTime: `${month}/${day}|${hours} ${ampm}`,
                temperature: roundedTemperature,
                energyUsage: Number(convertKWhToPreferredUnit(item.totalKWh, energyUnit).toFixed(2)),
            };
        }

        ), [energyUnit]);

    const rangeMap = {
        "Last 24 Hrs": "timePeriod24h",
        "Last 7 Days": "timePeriod7d",
        "Last 30 Days": "timePeriod30d",
    };

    const selectedRange = rangeMap[selectedOption] || "timePeriod30d";

    const selectedRangeMin = rangeMap[selectedOptionMin] || "timePeriod30d";
    const totalZoneEnergyLast30Days = formatEnergyValue(
        (energyUsage?.data?.timePeriod30d || []).reduce(
            (sum, item) => sum + (item.totalKWh || 0),
            0
        ),
        energyUnit
    );
    const avgPerUnitEnergyLast30Days = formatEnergyValue(
        (energyUsage?.data?.timePeriod30d || []).reduce(
            (sum, item) => sum + (item.avgPerUnitKWh || 0),
            0
        ),
        energyUnit
    );

    const selectedEnergyData = energyUsage?.data?.[selectedRangeMin] || [];
    const heatingSum = selectedEnergyData.reduce((sum, item) => sum + (item.heatingKWh || 0), 0);
    const coolingSum = selectedEnergyData.reduce((sum, item) => sum + (item.coolingKWh || 0), 0);

    const formattedData = useMemo(
        () => processChartData(energyUsage?.data?.[selectedRange] || []),
        [energyUsage?.data, processChartData, selectedRange]
    );

    const downloadCsvFile = useCallback((rows, fileNameSuffix) => {
        if (!rows.length || typeof window === "undefined") {
            return;
        }

        const csvContent = rows
            .map((row) => row.map(escapeCsvValue).join(","))
            .join("\n");
        const fileNameFleetPart = (selectedFleet?.name || "fleet")
            .trim()
            .replace(/[^a-z0-9]+/gi, "-")
            .replace(/^-+|-+$/g, "")
            .toLowerCase();
        const fileNameSuffixPart = fileNameSuffix
            .toLowerCase()
            .replace(/[^a-z0-9]+/gi, "-")
            .replace(/^-+|-+$/g, "");
        const downloadLink = document.createElement("a");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const objectUrl = window.URL.createObjectURL(blob);

        downloadLink.href = objectUrl;
        downloadLink.download = `${fileNameFleetPart || "fleet"}-${fileNameSuffixPart || "report"}.csv`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(objectUrl);
    }, [selectedFleet?.name]);

    const handleDownloadEnergyVsTemperatureCsv = useCallback(() => {
        if (!formattedData.length) {
            return;
        }

        downloadCsvFile([
            ["Date/Time", "Outdoor Temperature (°F)", `Energy Usage (${energyUnitLabel})`],
            ...formattedData.map((item) => [
                item.dateTime,
                item.temperature,
                item.energyUsage,
            ]),
        ], `energy-vs-temperature-${selectedOption}`);
    }, [downloadCsvFile, energyUnitLabel, formattedData, selectedOption]);

    const handleDownloadDeviceConnectivityCsv = useCallback(() => {
        if (!ConnectedChartData.length) {
            return;
        }

        downloadCsvFile([
            ["Connection Status", "Unit Count"],
            ...ConnectedChartData.map((item) => [item.name, item.value]),
        ], "device-connectivity");
    }, [ConnectedChartData, downloadCsvFile]);

    const RunTimechartData = useMemo(() => ([
        { label: "Heating", value: Number(convertKWhToPreferredUnit(heatingSum, energyUnit).toFixed(2)) },
        { label: "Cooling", cooling: Number(convertKWhToPreferredUnit(coolingSum, energyUnit).toFixed(2)) },
    ]), [coolingSum, energyUnit, heatingSum]);

    const handleDownloadTotalEnergyUsageCsv = useCallback(() => {
        if (!RunTimechartData.length) {
            return;
        }

        downloadCsvFile([
            ["Category", `Energy Usage (${energyUnitLabel})`],
            ...RunTimechartData.map((item) => [
                item.label,
                item.value ?? item.cooling ?? 0,
            ]),
        ], `total-energy-usage-${selectedOptionMin}`);
    }, [RunTimechartData, downloadCsvFile, energyUnitLabel, selectedOptionMin]);

    const getSelectedTimePeriodData = () => {
        switch (selectedOption) {
            case "Last 7 Days":
                return energyUsage?.data?.timePeriod7d;
            case "Last 30 Days":
                return energyUsage?.data?.timePeriod30d;
            default:
                return energyUsage?.data?.timePeriod24h;
        }
    };

    const getSelectedEnergyUsageData = () => {
        switch (selectedOptionMin) {
            case "Last 7 Days":
                return energyUsage?.data?.timePeriod7d;
            case "Last 30 Days":
                return energyUsage?.data?.timePeriod30d;
            default:
                return energyUsage?.data?.timePeriod24h;
        }
    };

    useFleetStatusCheck({
        unid: selectedFleet?.unid,
        isActive: ["/dashboard", "/"].includes(location.pathname),
    });

    return (
        <>
            {(loader || unitsFetching) ? (
                <div className="bg-white dark:darkThemeGradiantBg h-screen" data-testid="dashboard-loading">
                    <LoadingModal />
                </div>
            ) : totalUnitsCount === 0 ? (
                <div className="w-full sm:w-3/4 grow flex flex-col justify-center md:w-96 mx-auto" data-testid="dashboard-empty-state">
                    <div className="w-44 h-40 mx-auto">
                        <img
                            src={gotoUnits}
                            alt="go to units"
                            className="size-full h-auto mix-blend-multiply dark:hidden block"
                        />
                        <img
                            src={gotoUnitsInverse}
                            alt="go to units"
                            className="size-full h-auto hidden dark:block"
                        />
                    </div>
                    <div>
                        <h3 className="subtitle dark:text-white text-center text-blue font-semibold tracking-[0.036px] text-xl leading-5 lg:text-2xl lg:leading-6 mt-4">
                            You’re All Set—Almost
                        </h3>
                        <p className="text-dark-grey dark:text-white text-lg leading-6 font-normal tracking-[0.027px] text-center mt-4">
                            You haven’t added any units yet.
                        </p>
                        <p className="text-dark-grey dark:text-white text-lg leading-6 font-normal tracking-[0.027px] mt-6 text-center">
                            Let’s explore and connect your first unit to start using the full features.
                        </p>
                        <button
                            className="bg-theme text-white text-base font-semibold tracking-[-0.32px] leading-5 min-w-36 px-5 mx-auto h-10 rounded-lg mt-6 flex items-center justify-center hover:bg-theme/80 transition-all duration-300 ease-in-out"
                            onClick={handleNavigateToUnits}
                            data-testid="dashboard-goto-units-button"
                        >
                            Go to Assets - Manage Units
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="titleAndFilters">
                        <div className="flex flex-col gap-3 sm:flex-row justify-between items-start sm:items-center flex-wrap">
                            <h2 className="mainTitle text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]" data-testid="dashboard-title">Dashboard</h2>
                        </div>
                    </div>
                    <div className="mt-6 controlsHolder rounded-md" data-testid="dashboard-summary-cards">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <SummaryCard
                                data-testid="total-units-summary-card"
                                bgColor="bg-[#E3FCEB]"
                                icon={ <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
                                            <path d="M31.2505 40.8416C31.2505 36.0779 31.2505 33.6961 32.7304 32.2162C34.2103 30.7363 36.5921 30.7363 41.3558 30.7363C46.1195 30.7363 48.5014 30.7363 49.9813 32.2162C51.4611 33.6961 51.4611 36.0779 51.4611 40.8416V45.8943C51.4611 50.658 51.4611 53.0398 49.9813 54.5197C48.5014 55.9996 46.1195 55.9996 41.3558 55.9996C36.5921 55.9996 34.2103 55.9996 32.7304 54.5197C31.2505 53.0398 31.2505 50.658 31.2505 45.8943V40.8416Z" stroke="#00A653" stroke-width="3" />
                                            <path d="M2.19824 23.158C2.19824 27.9216 2.19824 30.3035 3.67813 31.7834C5.15803 33.2633 7.53987 33.2633 12.3036 33.2633C17.0673 33.2633 19.4491 33.2633 20.929 31.7834C22.4089 30.3035 22.4089 27.9216 22.4089 23.158V18.1053C22.4089 13.3416 22.4089 10.9598 20.929 9.47989C19.4491 8 17.0673 8 12.3036 8C7.53987 8 5.15803 8 3.67813 9.47989C2.19824 10.9598 2.19824 13.3416 2.19824 18.1053V23.158Z" stroke="#00A653" stroke-width="3" />
                                            <path d="M31.2505 15.579C31.2505 13.2247 31.2505 12.0476 31.6351 11.1191C32.1479 9.88104 33.1315 8.89742 34.3696 8.38461C35.2981 8 36.4752 8 38.8295 8H43.8822C46.2364 8 47.4135 8 48.3421 8.38461C49.5801 8.89742 50.5637 9.88104 51.0765 11.1191C51.4611 12.0476 51.4611 13.2247 51.4611 15.579C51.4611 17.9332 51.4611 19.1103 51.0765 20.0389C50.5637 21.2769 49.5801 22.2605 48.3421 22.7733C47.4135 23.158 46.2364 23.158 43.8822 23.158H38.8295C36.4752 23.158 35.2981 23.158 34.3696 22.7733C33.1315 22.2605 32.1479 21.2769 31.6351 20.0389C31.2505 19.1103 31.2505 17.9332 31.2505 15.579Z" stroke="#00A653" stroke-width="3" />
                                            <path d="M2.19824 48.4188C2.19824 50.7731 2.19824 51.9502 2.58285 52.8787C3.09567 54.1168 4.07929 55.1004 5.31734 55.6132C6.24587 55.9978 7.42299 55.9978 9.77724 55.9978H14.8299C17.1842 55.9978 18.3613 55.9978 19.2898 55.6132C20.5279 55.1004 21.5115 54.1168 22.0243 52.8787C22.4089 51.9502 22.4089 50.7731 22.4089 48.4188C22.4089 46.0646 22.4089 44.8875 22.0243 43.9589C21.5115 42.7209 20.5279 41.7373 19.2898 41.2245C18.3613 40.8398 17.1842 40.8398 14.8299 40.8398H9.77724C7.42299 40.8398 6.24587 40.8398 5.31734 41.2245C4.07929 41.7373 3.09567 42.7209 2.58285 43.9589C2.19824 44.8875 2.19824 46.0646 2.19824 48.4188Z" stroke="#00A653" stroke-width="3" />
                                        </svg>}
                                value={totalUnitsCount}
                                valueColor="text-resolved"
                                label="Total Units"
                            />
                            <SummaryCard
                                data-testid="total-zone-energy-summary-card"
                                bgColor="bg-[#E1F9FF]"
                                icon={<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
                                            <circle cx="32" cy="32" r="23" fill="#D5EEFF" stroke="#0057B8" strokeWidth="3" />
                                            <path d="M36.988 14L23.936 34.352H31.52L27.012 50L40.064 29.648H32.48L36.988 14Z" fill="#0057B8" />
                                        </svg>}
                                value={totalZoneEnergyLast30Days}
                                valueColor="text-[#0057B8]"
                                suffix={energyUnitLabel}
                                label={`Total Zone ${energyUnitLabel}`}
                                subLabel="(For last 30 days)"
                                />
                            <SummaryCard
                                data-testid="avg-per-unit-summary-card"
                                bgColor="bg-[#FFE4D1]"
                                icon={<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
                                            <circle cx="32" cy="32" r="23" fill="#FFF1E6" stroke="#D5671F" strokeWidth="3" />
                                            <path d="M18 26C19.7 24.5 21.9 24.5 23.6 26C25.3 27.5 27.5 27.5 29.2 26" stroke="#D5671F" strokeWidth="3" strokeLinecap="round" />
                                            <path d="M39.988 14L26.936 34.352H34.52L30.012 50L43.064 29.648H35.48L39.988 14Z" fill="#D5671F" />
                                        </svg>}
                                value={avgPerUnitEnergyLast30Days}
                                valueColor="text-[#D5671F]"
                                suffix={energyUnitLabel}
                                label={`Avg. per unit ${energyUnitLabel}`}
                                subLabel="(For last 30 days)"
                                />
                        </div>
                    </div>
                    <div className="mt-6 chartsWrapper">
                        <div className="flex gap-6 items-stretch h-full flex-wrap lg:flex-nowrap">
                            <div className="chartsHolder bg-white dark:darkThemeGradiantBg shadow-appShadow rounded-lg p-6 min-h-[360px] basis-full sm:basis-1/3 flex flex-col grow min-w-0 gap-2" data-testid="device-connectivity-panel">
                                <div className="flex items-center justify-between gap-4">
                                    <h5 className="chartsHeader text-black/[87] dark:text-white text-lg leading-5 lg:text-xl font-semibold lg:leading-6 tracking-[0.03px]">Device Connectivity</h5>
                                    <Button
                                        data-testid="device-connectivity-download-button"
                                        type="button"
                                        onClick={handleDownloadDeviceConnectivityCsv}
                                        disabled={!ConnectedChartData.length}
                                        aria-label="Download Device Connectivity chart data as CSV"
                                        title="Download CSV"
                                        className={`flex h-10 w-10 items-center justify-center rounded-lg border transition-colors ${
                                            ConnectedChartData.length
                                                ? "border-[#E1E5EA] text-theme hover:bg-[#FFF4EF] dark:border-darkThemeBorder dark:text-[#FF8C65]"
                                                : "cursor-not-allowed border-[#E1E5EA] text-[#BDBDBD] dark:border-darkThemeBorder dark:text-white/30"
                                        }`}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            aria-hidden="true"
                                        >
                                            <path d="M11.6667 1.66699H5.83333C4.91286 1.66699 4.16667 2.41318 4.16667 3.33366V16.667C4.16667 17.5875 4.91286 18.3337 5.83333 18.3337H14.1667C15.0871 18.3337 15.8333 17.5875 15.8333 16.667V5.83366L11.6667 1.66699Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M11.6667 1.66699V5.83366H15.8333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M10 8.33301V13.333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                            <path d="M8.125 11.458L10 13.333L11.875 11.458" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M7.5 15H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                        </svg>
                                    </Button>
                                </div>
                                <div className="chartsContent grow">
                                    <div className="h-full flex items-center justify-center">

                                        {totalUnitsCount === 0 ? (
                                            <p className="text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.027px]" data-testid="device-connectivity-no-data">No data available</p>
                                        ) : (
                                            <DonutChart data={ConnectedChartData} onSliceClick={handleDonutSliceClick} data-testid="device-connectivity-chart" />
                                        )}

                                    </div>
                                </div>
                            </div>
                            <div className="chartsHolder bg-white dark:darkThemeGradiantBg shadow-appShadow rounded-lg p-6 min-h-[360px] basis-full sm:basis-1/3 pb-6 flex flex-col grow min-w-0 gap-6" data-testid="alerts-panel">
                                <div className="flex flex-col gap-4 h-full">
                                    <div className="flex items-center justify-between gap-4 pb-2.5">
                                        <h5 className="chartsHeader text-black/[87] dark:text-white text-lg leading-5 lg:text-xl font-semibold lg:leading-6 tracking-[0.03px]">Alerts</h5>
                                        <button className="text-theme text-xs lg:text-sm lg:leading-7 font-semibold leading-5 tracking-[0.03px]" onClick={handleNavigateToAlarms} data-testid="alerts-view-all-button">View All</button>
                                    </div>

                                    {alarmsdata?.length !== 0 ? (

                                        <div className="chartsContent grow flex flex-col gap-4" data-testid="alerts-list">
                                            {alarmsdata.slice(0, 5).map((item, index) => {
                                                const videoUrl = getAlertVideoUrl(item?.type);
                                                return (
                                                    <div
                                                        key={index}
                                                        className="flex items-start gap-4 group w-full min-w-0 cursor-pointer"
                                                        data-testid={`alert-row-${item?.unit?.serialNumber || index}`}
                                                        {...linkKeyboardProps(() => handleNavigateToUnit(item?.unit?.serialNumber))}
                                                    >
                                                        <div
                                                            className={`iconHolder basis-5 grow-0 shrink-0 ${item.severity === "warning" ? "pt-[6px]" : "pt-1.5"}`}
                                                        >
                                                            <AlertIcon severity={item.severity} data-testid={`alert-icon-${item?.unit?.serialNumber || index}`} />
                                                        </div>
                                                        <div className="flex items-center gap-2 border-b border-b-grey pb-4 grow min-w-0 whitespace-nowrap group-last:border-b-0">
                                                            <span className={`text-${item.severity} dark:text-white text-base font-normal leading-6 tracking-[0.024px] shrink-0 truncate`} data-testid={`alert-type-${item?.unit?.serialNumber || index}`}>
                                                                {item.type}
                                                            </span>
                                                            <p className="text-[#1E555C] dark:text-white text-sm font-medium leading-5 tracking-[0.027px] min-w-0 truncate" title={`${item.unit.metadata.name} (${item.unit.serialNumber})`} data-testid={`alert-unit-name-${item?.unit?.serialNumber || index}`}>
                                                                {item.unit.metadata.name} ({item.unit.serialNumber})
                                                            </p>
                                                            <ConnectionStatusIcon
                                                                connected={item?.unit?.connected === true}
                                                                connectionType={item?.unit?.connectionType}
                                                                className="size-5 shrink-0"
                                                                hasActiveAlert={item?.resolvedAt == null}
                                                                data-testid={`connection-status-${item?.unit?.serialNumber || "alarm"}-${index}`}
                                                            />
                                                            <p className="timeStamp text-[#7F7F7F] dark:text-white/60 text-xs font-normal leading-4 tracking-[0.02px] shrink-0 whitespace-nowrap" data-testid={`alert-timestamp-${item?.unit?.serialNumber || index}`}>
                                                                {item?.startedAt
                                                                    ? <>
                                                                        {new Date(item?.startedAt).toLocaleDateString("en-US", {
                                                                            timeZone: timeZone,
                                                                            day: "2-digit",
                                                                            month: "2-digit",
                                                                            year: "2-digit",
                                                                        })}&nbsp;&nbsp;&nbsp;&nbsp;
                                                                        {new Date(item?.startedAt).toLocaleTimeString("en-US", {
                                                                            timeZone: timeZone,
                                                                            hour12: false,
                                                                            hour: "2-digit",
                                                                            minute: "2-digit",
                                                                            second: "2-digit",
                                                                        })}
                                                                    </>
                                                                    : ''}
                                                            </p>
                                                            {videoUrl && (
                                                                <Button
                                                                    data-testid={`alert-video-button-${item?.unit?.serialNumber || index}`}
                                                                    className="inline-flex items-center justify-center shrink-0 ml-2 text-theme hover:text-theme/80 transition-colors duration-200"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        window.open(videoUrl, "_blank", "noopener,noreferrer");
                                                                    }}
                                                                    aria-label="Watch instructional video"
                                                                    title="Watch instructional video"
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="32"
                                                                        height="32"
                                                                        viewBox="0 0 16 16"
                                                                        fill="none"
                                                                    >
                                                                        <circle cx="8" cy="8" r="7.25" stroke="currentColor" strokeWidth="1.5" />
                                                                        <path d="M6.5 5.75L10.25 8L6.5 10.25V5.75Z" fill="currentColor" />
                                                                    </svg>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="h-full flex items-center justify-center">
                                            <p className="text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.027px]" data-testid="alerts-empty-state">No Alerts</p>
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-6 chartsWrapper">
                        <div className="chartsHolder bg-white dark:darkThemeGradiantBg shadow-appShadow rounded-lg p-6 min-h-[360px] basis-full sm:basis-1/3 flex flex-col grow gap-6" data-testid="energy-vs-temperature-panel">
                            <div className="flex justify-between items-center max-[480px]:flex-col max-[480px]:justify-start gap-4">
                                <h5 className="chartsHeader text-black/[87] dark:text-white text-lg leading-5 lg:text-xl font-semibold lg:leading-6 tracking-[0.03px] w-full">Energy Usage Vs Temperature </h5>
                                <div className="grow-0 ml-auto flex items-center gap-3">
                                    <Button
                                        data-testid="energy-vs-temperature-download-button"
                                        type="button"
                                        onClick={handleDownloadEnergyVsTemperatureCsv}
                                        disabled={!formattedData.length}
                                        aria-label="Download Energy Usage Vs Temperature chart data as CSV"
                                        title="Download CSV"
                                        className={`flex h-10 w-10 items-center justify-center rounded-lg border transition-colors ${
                                            formattedData.length
                                                ? "border-[#E1E5EA] text-theme hover:bg-[#FFF4EF] dark:border-darkThemeBorder dark:text-[#FF8C65]"
                                                : "cursor-not-allowed border-[#E1E5EA] text-[#BDBDBD] dark:border-darkThemeBorder dark:text-white/30"
                                        }`}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M11.6667 1.66699H5.83333C4.91286 1.66699 4.16667 2.41318 4.16667 3.33366V16.667C4.16667 17.5875 4.91286 18.3337 5.83333 18.3337H14.1667C15.0871 18.3337 15.8333 17.5875 15.8333 16.667V5.83366L11.6667 1.66699Z"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                d="M11.6667 1.66699V5.83366H15.8333"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                d="M10 8.33301V13.333"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                            />
                                            <path
                                                d="M8.125 11.458L10 13.333L11.875 11.458"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                d="M7.5 15H12.5"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </Button>
                                    <Daysfilterdropdown
                                        data-testid="energy-vs-temperature-days-filter"
                                        selectedOption={selectedOption}
                                        setSelectedOption={setSelectedOption}
                                    />
                                </div>
                            </div>
                            <div className={`chartsContent grow min-w-0 ${(getSelectedTimePeriodData() !== null) ? "" : "flex justify-center items-center"}`}>
                                <div className="h-full w-full min-w-0 flex items-center justify-center">

                                    {getSelectedTimePeriodData() !== null ? (
                                        <DualAxisLineChart
                                            data-testid="energy-vs-temperature-chart"
                                            data={formattedData}
                                            energyUnit={energyUnit}
                                            energyUnitLabel={energyUnitLabel}
                                        />
                                    ) : (
                                        <p className="text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.027px]" data-testid="energy-vs-temperature-no-data">No data available</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-6 mb-6 chartsWrapper">
                        <div className="flex gap-6 items-stretch h-full flex-wrap sm:flex-nowrap">
                            <div className="chartsHolder bg-white dark:darkThemeGradiantBg shadow-appShadow rounded-lg p-6 min-h-48 basis-full sm:basis-1/3 flex flex-col grow gap-6" data-testid="weather-panel">
                                <h5 className="chartsHeader text-black/[87] dark:text-white text-lg leading-5 lg:text-xl font-semibold lg:leading-6 tracking-[0.03px]">Weather</h5>
                                {totalUnitsCount > 0 ? (
                                    <div className="chartsContent grow">
                                        <div className="h-full w-full">
                                            <div className="dashboard-section p-2">
                                                <WeatherWidget
                                                    key={selectedFleet?.weatherWidgetId} // Force remount when ID changes
                                                    selectedFleet={selectedFleet}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="chartsContent grow">
                                        <div className="h-full flex items-center justify-center">
                                            <p className="text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.027px]" data-testid="weather-no-data">No data available</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="chartsHolder bg-white dark:darkThemeGradiantBg shadow-appShadow rounded-lg p-6 min-h-48 basis-full sm:basis-1/3 flex flex-col grow gap-6" data-testid="total-energy-usage-panel">
                                <div className="flex justify-between items-center flex-col md:flex-row max-[480px]:justify-start gap-4">
                                <h5 className="chartsHeader text-black/[87] dark:text-white text-lg leading-5 lg:text-xl font-semibold lg:leading-6 tracking-[0.03px] w-full">Total Energy Usage</h5>
                                    <div className="grow-0 ml-auto flex items-center gap-3">
                                        <Button
                                            data-testid="total-energy-usage-download-button"
                                            type="button"
                                            onClick={handleDownloadTotalEnergyUsageCsv}
                                            disabled={!RunTimechartData.length}
                                            aria-label="Download Total Energy Usage chart data as CSV"
                                            title="Download CSV"
                                            className={`flex h-10 w-10 items-center justify-center rounded-lg border transition-colors ${
                                                RunTimechartData.length
                                                    ? "border-[#E1E5EA] text-theme hover:bg-[#FFF4EF] dark:border-darkThemeBorder dark:text-[#FF8C65]"
                                                    : "cursor-not-allowed border-[#E1E5EA] text-[#BDBDBD] dark:border-darkThemeBorder dark:text-white/30"
                                            }`}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                                aria-hidden="true"
                                            >
                                                <path d="M11.6667 1.66699H5.83333C4.91286 1.66699 4.16667 2.41318 4.16667 3.33366V16.667C4.16667 17.5875 4.91286 18.3337 5.83333 18.3337H14.1667C15.0871 18.3337 15.8333 17.5875 15.8333 16.667V5.83366L11.6667 1.66699Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M11.6667 1.66699V5.83366H15.8333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M10 8.33301V13.333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                <path d="M8.125 11.458L10 13.333L11.875 11.458" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M7.5 15H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                            </svg>
                                        </Button>
                                        <Daysfilterdropdown
                                            data-testid="total-energy-usage-days-filter"
                                            selectedOption={selectedOptionMin}
                                            setSelectedOption={setSelectedOptionMin}
                                        />
                                    </div>
                                </div>
                                <div className="chartsContent grow">
                                    <div className="h-full flex items-center justify-center">
                                        {getSelectedEnergyUsageData() !== null ? (
                                            <RuntimeBarChart
                                                data-testid="total-energy-usage-chart"
                                                data={RunTimechartData}
                                                energyUnit={energyUnit}
                                                energyUnitLabel={energyUnitLabel}
                                            />
                                        ) : (
                                            <p className="text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.027px]" data-testid="total-energy-usage-no-data">No data available</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    )
}

export default Dashboard;
